import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FileText, Eye } from 'lucide-react';

import Tooltip from '../../../components/ui/Tooltip';
import IconButton from '../../../components/ui/IconButton';
import DocumentViewerModal from '../../../components/ui/DocumentViewerModal';

const Wrapper = styled.div`
  padding: 8px 0 32px;
`;

const Card = styled.section`
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 10px;
  overflow: hidden;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 18px;
  border-bottom: 1px solid #E8ECF0;
  background: #FAFBFC;
`;

const FileName = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const PreviewHint = styled.div`
  padding: 64px 20px;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.dark.little};
  font-size: 13px;
`;

const Empty = styled.div`
  padding: 64px 20px;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const extractFileName = (url) => {
  if (!url) return '';
  try {
    const u = new URL(url);
    const parts = u.pathname.split('/').filter(Boolean);
    return decodeURIComponent(parts[parts.length - 1] || 'resume');
  } catch {
    return url.split('/').pop() || 'resume';
  }
};

export default function ApplicationResumeTab({ candidate }) {
  const { t } = useTranslation();
  const url = candidate?.resumeFileUrl;
  // Açık olduğunda DocumentViewerModal'ı besler. Aday özgeçmişi özel sınıfa taşındı
  // (StorageVisibility.Private — candidateResume amacı) — kayıtlı adres artık ne bir <iframe>'e
  // ne bir <a href>'e doğrudan verilebilir. DocumentViewerModal kendisi `/v1/storage/view`
  // üzerinden çözer (beş özlük-dosyası yüzeyiyle aynı desen); ürün sahibi kararı: belgeler
  // ASLA yeni sekmede açılmaz.
  const [viewing, setViewing] = useState(false);

  if (!candidate) return <Empty>{t('common.loading', { defaultValue: 'Yükleniyor...' })}</Empty>;
  if (!url) return (
    <Wrapper>
      <Card>
        <Empty>{t('ats.application.resume.notUploaded')}</Empty>
      </Card>
    </Wrapper>
  );

  return (
    <Wrapper>
      <Card>
        <Toolbar>
          <FileName>
            <FileText size={16} />
            {extractFileName(url)}
          </FileName>
          <Actions>
            <Tooltip text={t('ats.application.resume.view')}>
              <IconButton
                data-testid="resume-view-trigger"
                aria-label={t('ats.application.resume.view')}
                onClick={() => setViewing(true)}
              >
                <Eye size={16} />
              </IconButton>
            </Tooltip>
          </Actions>
        </Toolbar>
        <PreviewHint>{t('ats.application.resume.previewHint')}</PreviewHint>
      </Card>
      <DocumentViewerModal
        open={viewing}
        fileUrl={url}
        title={t('ats.application.resume.title')}
        onClose={() => setViewing(false)}
      />
    </Wrapper>
  );
}
